import { Fragment, useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AvDot } from "@/components/AvailabilityButtons";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";
import { dateLocale } from "@/lib/i18n";

const CAT_DOT = { repetition: "bg-emerald-400", prestation: "bg-rose-400", autre: "bg-sky-400" };
const LEGACY_MAIN = { concert: "prestation", concours: "prestation", evenement: "autre" };
const mainOf = (k) => (CAT_DOT[k] ? k : LEGACY_MAIN[k] || "autre");
const CYCLE = { none: "yes", yes: "maybe", maybe: "no", no: "none" };

export default function PresencesPage() {
  const { user, t, lang } = useAuth();
  const [data, setData] = useState({ events: [], members: [], availability: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("avenir");
  const [params, setParams] = useSearchParams();
  const focusId = params.get("event");
  // Zoom du tableau (dézoom par défaut à 85 %), réglable et mémorisé sur l'appareil
  const [zoom, setZoom] = useState(() => {
    const z = parseFloat(localStorage.getItem("presence_zoom"));
    return isNaN(z) ? 0.85 : Math.min(1, Math.max(0.6, z));
  });
  useEffect(() => { localStorage.setItem("presence_zoom", String(zoom)); }, [zoom]);
  const zoomBy = (d) => setZoom((z) => Math.min(1, Math.max(0.6, Math.round((z + d) * 100) / 100)));

  const load = useCallback(() => {
    api.get("/presence")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.friendly))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const upcomingEvents = data.events.filter((e) => (e.end_date || e.date) >= today);
  const pastEvents = data.events.filter((e) => (e.end_date || e.date) < today).slice().reverse();
  const focusEv = focusId ? data.events.find((e) => e.id === focusId) : null;
  const shownEvents = focusEv ? [focusEv] : (tab === "avenir" ? upcomingEvents : pastEvents);
  const nextId = !focusEv && tab === "avenir" ? upcomingEvents[0]?.id : undefined;

  const cycle = async (ev, member) => {
    if (member.id !== user.id) return;
    const cur = data.availability[ev.id]?.[member.id] || "none";
    const next = CYCLE[cur];
    try {
      await api.post(`/events/${ev.id}/availability`, { status: next });
      if (next === "none") {
        setData((d) => {
          const av = { ...(d.availability[ev.id] || {}) };
          delete av[member.id];
          return { ...d, availability: { ...d.availability, [ev.id]: av } };
        });
        return;
      }
      setData((d) => ({
        ...d,
        availability: { ...d.availability, [ev.id]: { ...(d.availability[ev.id] || {}), [member.id]: next } },
      }));
    } catch (e) { toast.error(e.friendly); }
  };

  // Group members by instrument (robuste, non contigu)
  const groupsMap = new Map();
  data.members.forEach((m) => {
    const instr = m.instrument || t("pres.no_instrument");
    if (!groupsMap.has(instr)) groupsMap.set(instr, []);
    groupsMap.get(instr).push(m);
  });
  const groups = [...groupsMap.entries()].map(([instrument, members]) => ({ instrument, members }));

  return (
    <div className="space-y-4" data-testid="presence-page">
      <div>
        <h2 className="font-heading text-2xl font-bold">{t("pres.title")}</h2>
        <p className="text-sm t-secondary mt-1">{t("pres.subtitle")}</p>
      </div>
      {focusEv && (
        <section className="app-card p-4 space-y-3" data-testid="presence-event-focus">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-heading font-bold" data-testid="presence-focus-title">{focusEv.title}</h3>
              <p className="text-xs t-secondary mt-0.5">
                {new Date(focusEv.date + "T00:00").toLocaleDateString(dateLocale(lang), { weekday: "long", day: "numeric", month: "long" })}
                {focusEv.start_time && ` • ${focusEv.start_time}`}{focusEv.location && ` • ${focusEv.location}`}
              </p>
            </div>
            <button
              onClick={() => { params.delete("event"); setParams(params, { replace: true }); }}
              className="text-xs brand hover:underline shrink-0 py-1"
              data-testid="presence-focus-close"
            >
              {t("pres.full_view")}
            </button>
          </div>
          {groups.map((g) => {
            const yesN = g.members.filter((m) => (data.availability[focusEv.id] || {})[m.id] === "yes").length;
            return (
              <div key={g.instrument} data-testid={`presence-focus-group-${g.instrument}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider accent">{g.instrument}</span>
                  <span className="text-xs t-secondary" data-testid={`presence-focus-count-${g.instrument}`}>
                    <span className={yesN === g.members.length ? "text-emerald-400 font-semibold" : ""}>{yesN} {t("pres.present_label")}</span> / {g.members.length}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {g.members.map((m) => {
                    const st = (data.availability[focusEv.id] || {})[m.id] || "none";
                    return (
                      <span key={m.id} className="inline-flex items-center gap-1.5 text-xs border border-app rounded-full px-2.5 py-1" data-testid={`presence-focus-member-${m.id}`}>
                        <AvDot status={st} size="h-3 w-3" /> {m.prenom} {m.nom}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      )}
      {!focusEv && (
      <div className="flex gap-2" data-testid="presence-tabs">
        <button
          onClick={() => setTab("avenir")}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${tab === "avenir" ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
          data-testid="presence-tab-avenir"
        >
          {t("pres.tab_upcoming")} ({upcomingEvents.length})
        </button>
        <button
          onClick={() => setTab("passee")}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${tab === "passee" ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
          data-testid="presence-tab-effectuee"
        >
          {t("pres.tab_done")} ({pastEvents.length})
        </button>
      </div>
      )}
      <div className="flex items-center justify-end gap-1.5" data-testid="presence-zoom">
        <button
          onClick={() => zoomBy(-0.05)}
          disabled={zoom <= 0.6}
          className="h-8 w-8 rounded-lg border border-app t-secondary hover:bg-white/5 hover:text-white transition-colors inline-flex items-center justify-center disabled:opacity-30"
          title={t("pres.zoom_out")} aria-label={t("pres.zoom_out")}
          data-testid="presence-zoom-out"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="text-xs t-secondary w-11 text-center tabular-nums" data-testid="presence-zoom-level">{Math.round(zoom * 100)} %</span>
        <button
          onClick={() => zoomBy(0.05)}
          disabled={zoom >= 1}
          className="h-8 w-8 rounded-lg border border-app t-secondary hover:bg-white/5 hover:text-white transition-colors inline-flex items-center justify-center disabled:opacity-30"
          title={t("pres.zoom_in")} aria-label={t("pres.zoom_in")}
          data-testid="presence-zoom-in"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <div className="app-card overflow-x-auto" style={{ zoom }} data-testid="presence-matrix">
        {loading && <p className="t-secondary text-sm p-6 text-center" data-testid="presence-loading">{t("pres.loading")}</p>}
        {!loading && (
        <table className="w-full text-sm" data-testid="presence-table">
          <thead>
            <tr className="border-b border-app">
              <th className="text-left p-3 font-heading sticky left-0 card-bg min-w-40">{t("pres.member")}</th>
              {shownEvents.map((ev) => (
                <th key={ev.id} className={`p-2 text-center min-w-24 ${ev.id === nextId ? "bg-emerald-500/10" : ""} ${ev.date < today ? "opacity-50" : ""}`} data-testid={`presence-col-${ev.id}`}>
                  <div className="flex flex-col items-center gap-1">
                    <span className={`h-2 w-2 rounded-full ${CAT_DOT[mainOf(ev.category)]}`} />
                    <span className="text-xs font-semibold whitespace-nowrap">
                      {new Date(ev.date + "T00:00").toLocaleDateString(dateLocale(lang), { day: "numeric", month: "short" })}
                    </span>
                    <span className="text-[10px] t-secondary font-normal max-w-24 truncate">{ev.subcategory || ev.location || ev.title}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.instrument}>
                <tr key={`hdr-${g.instrument}`} className="border-b border-app/50">
                  <td colSpan={shownEvents.length + 1} className="px-3 py-2 text-xs font-bold uppercase tracking-wider accent sticky left-0 card-bg">
                    {g.instrument}
                  </td>
                </tr>
                {g.members.map((m) => (
                  <tr key={m.id} className="border-b border-app/30 hover:bg-white/[0.02]" data-testid={`presence-row-${m.id}`}>
                    <td className="p-3 sticky left-0 card-bg">
                      <span className="font-medium">{m.prenom} {m.nom}</span>
                      {m.id === user.id && <span className="text-xs brand ml-2">{t("pres.you")}</span>}
                    </td>
                    {shownEvents.map((ev) => {
                      const st = data.availability[ev.id]?.[m.id] || "none";
                      const clickable = m.id === user.id && ev.date >= today;
                      return (
                        <td key={ev.id} className={`p-2 text-center ${ev.id === nextId ? "bg-emerald-500/10" : ""} ${ev.date < today ? "opacity-50" : ""}`}>
                          <button
                            onClick={() => clickable && cycle(ev, m)}
                            className={`inline-flex p-1.5 rounded-full ${clickable ? "hover:bg-white/10 cursor-pointer" : "cursor-default"}`}
                            data-testid={`presence-cell-${ev.id}-${m.id}`}
                            disabled={!clickable}
                          >
                            <AvDot status={st} size="h-4 w-4" />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        )}
        {!loading && shownEvents.length === 0 && (
          <p className="text-sm t-secondary p-6 text-center" data-testid="presence-empty">
            {tab === "avenir" ? t("pres.empty_upcoming") : t("pres.empty_past")}
          </p>
        )}
      </div>
      <div className="flex gap-4 text-xs t-secondary flex-wrap">
        <span className="flex items-center gap-1.5"><AvDot status="yes" /> {t("avail.yes")}</span>
        <span className="flex items-center gap-1.5"><AvDot status="maybe" /> {t("avail.maybe")}</span>
        <span className="flex items-center gap-1.5"><AvDot status="no" /> {t("avail.no")}</span>
        <span className="flex items-center gap-1.5"><AvDot status="none" /> {t("avail.none")}</span>
      </div>
    </div>
  );
}
